import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { ChevronLeft, FileText, History } from 'lucide-react';
import {
  getFunctions,
  httpsCallable,
  FunctionsErrorCode,
} from 'firebase/functions';
import { Button, Input, Dropdown, Table } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { db, app } from '../lib/firebase';
import {
  SessionPriceTier,
  Session,
  Patient,
  ChangeLogEntry,
} from '../types/firestoreTypes';
import {
  SESSION_STATUS,
  SESSION_TYPE,
  SESSION_STATUS_OPTIONS,
  SESSION_TYPE_OPTIONS,
  SessionStatus,
  SessionType,
} from '../types/constants';
import { useAnalytics } from '../hooks/useAnalytics';
import { useFeatureUpgradeModal } from '../contexts/FeatureUpgradeModalContext';

interface SessionFormData {
  status: SessionStatus;
  sessionType: SessionType;
  priceTierId: string;
  notes: string;
}

interface GenerateInvoiceResponse {
  success: boolean;
  invoiceId?: number;
  invoiceNumber?: string;
  error?: string;
  sendStatus?: string;
}

const SessionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { patientId, sessionId } = useParams<{
    patientId: string;
    sessionId: string;
  }>();
  const navigate = useNavigate();
  const { user, therapist } = useAuth();
  const { addToast } = useToast();
  const functions = getFunctions(app, 'europe-west1');
  const { trackEvent } = useAnalytics();
  const { openUpgradeModal } = useFeatureUpgradeModal();

  const [session, setSession] = useState<Session | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [priceTiers, setPriceTiers] = useState<SessionPriceTier[]>([]);
  const [formData, setFormData] = useState<Partial<SessionFormData>>({});

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [changeLogEntries, setChangeLogEntries] = useState<ChangeLogEntry[]>([]);
  const [isLoadingLog, setIsLoadingLog] = useState(false);
  const [logError, setLogError] = useState<string | undefined>(undefined);

  const setTranslatedError = (key: string, options?: Record<string, unknown>) => {
    setError(t(key, options));
  };
  const setTranslatedLogError = (key: string, options?: Record<string, unknown>) => {
    setLogError(t(key, options));
  };

  const refetchSessionData = useCallback(async () => {
    if (!user || !sessionId) return;
    console.log('Refetching session data...');
    try {
      const sessionDoc = await getDoc(doc(db, 'sessions', sessionId));
      if (!sessionDoc.exists()) {
        setTranslatedError('sessionDetailPage.errors.notFound');
        return;
      }
      const sessionData = {
        id: sessionDoc.id,
        ...sessionDoc.data(),
      } as Session;
      if (sessionData.therapistId !== user.uid) {
        setTranslatedError('sessionDetailPage.errors.permissionDenied');
        return;
      }
      setSession(sessionData);
      setFormData({
        status: sessionData.status,
        sessionType: sessionData.sessionType,
        priceTierId: sessionData.priceTierId || '',
        notes: sessionData.notes || '',
      });
    } catch (err) {
      console.error('Error refetching session:', err);
      addToast({
        type: 'error',
        message: t('sessionDetailPage.toasts.refreshFailed'),
      });
    }
  }, [user, sessionId, t, addToast]);

  const fetchChangeLog = useCallback(async () => {
    if (!sessionId) return;
    console.log('Fetching change log...');
    setIsLoadingLog(true);
    setLogError(null);
    try {
      const logQuery = query(
        collection(db, 'sessions', sessionId, 'changeLog'),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(logQuery);
      const logs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ChangeLogEntry[];
      setChangeLogEntries(logs);
    } catch (err) {
      console.error('Error fetching change log:', err);
      setTranslatedLogError('sessionDetailPage.errors.logLoadFailed');
    } finally {
      setIsLoadingLog(false);
    }
  }, [sessionId, t]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !sessionId || !patientId) {
        setIsLoading(false);
        return;
      }
      console.log('Fetching initial session/patient/pricetier data...');
      setIsLoading(true);
      setError(null);
      setLogError(null);

      try {
        const [sessionDoc, patientDoc, priceTiersSnapshot] = await Promise.all([
          getDoc(doc(db, 'sessions', sessionId)),
          getDoc(doc(db, 'patients', patientId)),
          getDocs(
            query(
              collection(db, 'sessionPriceTiers'),
              where('therapistId', '==', user.uid),
              where('isArchived', '==', false),
              orderBy('tierName')
            )
          ),
        ]);

        if (!sessionDoc.exists()) {
          setTranslatedError('sessionDetailPage.errors.notFound');
          setIsLoading(false);
          return;
        }
        const sessionData = {
          id: sessionDoc.id,
          ...sessionDoc.data(),
        } as Session;
        if (sessionData.therapistId !== user.uid) {
          setTranslatedError('sessionDetailPage.errors.permissionDenied');
          setIsLoading(false);
          return;
        }

        if (!patientDoc.exists()) {
          setTranslatedError('sessionDetailPage.errors.patientNotFound');
          setIsLoading(false);
          return;
        }
        const patientData = {
          id: patientDoc.id,
          ...patientDoc.data(),
        } as Patient;

        const fetchedPriceTiers = priceTiersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SessionPriceTier[];

        setSession(sessionData);
        setPatient(patientData);
        setFormData({
          status: sessionData.status,
          sessionType: sessionData.sessionType,
          priceTierId: sessionData.priceTierId || '',
          notes: sessionData.notes || '',
        });
        setPriceTiers(fetchedPriceTiers);
      } catch (err) {
        console.error('Error fetching session detail data:', err);
        setTranslatedError('sessionDetailPage.errors.loadFailed');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user, sessionId, patientId, t]);

  useEffect(() => {
    if (sessionId) {
      fetchChangeLog();
    }
  }, [sessionId, fetchChangeLog]);

  const handleSaveChanges = async () => {
    if (!user || !session || !formData || Object.keys(formData).length === 0)
      return;

    const updateData: Partial<Session> = {};
    let changed = false;

    if (formData.status && formData.status !== session.status) {
      updateData.status = formData.status;
      changed = true;
    }
    if (formData.sessionType && formData.sessionType !== session.sessionType) {
      updateData.sessionType = formData.sessionType;
      changed = true;
    }
    if (formData.notes !== (session.notes || '')) {
      updateData.notes = formData.notes || null;
      changed = true;
    }
    if (
      formData.priceTierId &&
      formData.priceTierId !== (session.priceTierId || '')
    ) {
      const selectedTier = priceTiers.find(
        (tier) => tier.id === formData.priceTierId
      );
      if (selectedTier) {
        updateData.priceTierId = selectedTier.id;
        updateData.price = {
          amount: selectedTier.price,
          currency: selectedTier.currency,
        };
        updateData.durationMinutes = selectedTier.durationMinutes;
        changed = true;
      } else {
        addToast({
          type: 'error',
          message: t('sessionDetailPage.errors.invalidPriceTier'),
        });
        return;
      }
    } else if (!formData.priceTierId && session.priceTierId) {
      updateData.priceTierId = null;
      updateData.price = null;
      updateData.durationMinutes = 0;
      changed = true;
    }

    if (!changed) {
      addToast({
        type: 'info',
        message: t('sessionDetailPage.toasts.noChanges'),
      });
      return;
    }

    setIsSaving(true);
    try {
      const sessionRef = doc(db, 'sessions', session.id);
      await updateDoc(sessionRef, updateData);

      await refetchSessionData();
      await fetchChangeLog();

      addToast({
        type: 'success',
        message: t('sessionDetailPage.toasts.updateSuccess'),
      });
    } catch (err) {
      console.error('Error updating session:', err);
      addToast({
        type: 'error',
        message: t('sessionDetailPage.toasts.updateFailed'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateInvoice = async () => {
    // --- Feature Gate Checks (Should be Existing from Prompt 11/12) ---
    const hasBillingAccess = therapist?.packageFeaturesSnapshot?.BILLING_ACCESS_ENABLED;
    if (therapist && typeof hasBillingAccess === 'boolean' && !hasBillingAccess) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.billingAccess',
        customMessageKey: 'featureUpgradeModal.messages.billingAccessDisabled',
      });
      return;
    }

    const hasSingleInvoiceAccess = therapist?.packageFeaturesSnapshot?.INVOICE_SINGLE_SESSION;
    if (therapist && typeof hasSingleInvoiceAccess === 'boolean' && !hasSingleInvoiceAccess) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.singleSessionInvoicing',
        customMessageKey: 'featureUpgradeModal.messages.singleSessionInvoicingDisabled',
      });
      return;
    }
    // --- End Feature Gate Checks ---

    if (!session || !patient) {
      console.error("Session or patient data is not available for invoice generation.");
      addToast({ type: 'error', message: t('sessionDetailPage.errors.notFound') });
      return;
    }

    // --- NEW Operational Prerequisite Checks ---
    // 1. Check session status
    if (session.status !== SESSION_STATUS.COMPLETED) {
      addToast({
        type: 'warning',
        message: t('sessionDetailPage.toasts.sessionNotCompleted'),
      });
      return;
    }

    // 2. Check patient billing profile ID AND address completeness
    const isAddressComplete = patient?.address &&
                            patient.address.street &&
                            patient.address.city &&
                            patient.address.country &&
                            patient.address.postalCode;

    if (!patient?.billingProfileId || !isAddressComplete) {
      addToast({
        type: 'warning',
        message: t('sessionDetailPage.toasts.patientBillingInfoIncomplete'),
      });
      return;
    }
    // --- End Operational Prerequisite Checks ---

    // If all checks pass, proceed with actual invoice generation:
    setIsGeneratingInvoice(true);
    setError(null);

    try {
      const generateInvoiceCallable = httpsCallable<
        { sessionId: string },
        GenerateInvoiceResponse
      >(functions, 'generateInvoice');

      const result = await generateInvoiceCallable({ sessionId: session.id });
      const response = result.data;

      if (response.success && response.invoiceId) {
        trackEvent('invoice_generated', {
          session_id: session.id,
          patient_id: patient.id,
          invoice_id: response.invoiceId,
          invoice_number: response.invoiceNumber,
        });
        addToast({
          type: 'success',
          message: t('sessionDetailPage.success.invoiceGenerated', {
            invoiceNumber: response.invoiceNumber || response.invoiceId,
            sendStatus: response.sendStatus || 'unknown',
          }),
        });
        await refetchSessionData();
        await fetchChangeLog();
      } else {
        const errorMessage = response.error || t('sessionDetailPage.errors.invoiceFailedGeneric');
        addToast({ type: 'error', message: errorMessage });
        setError(errorMessage);
        trackEvent('error_occurred', {
          error_message: errorMessage,
          context: 'invoice_generation',
          session_id: session.id,
          patient_id: patient.id,
        });
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      const generalErrorMessage = t('sessionDetailPage.errors.invoiceFailedUnexpected');
      addToast({ type: 'error', message: generalErrorMessage });
      setError(generalErrorMessage);
      trackEvent('error_occurred', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        context: 'invoice_generation',
        session_id: session.id,
        patient_id: patient.id,
      });
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const translatedStatusOptions = React.useMemo(
    () =>
      SESSION_STATUS_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`status.${option.value}`, option.label),
      })),
    [t]
  );

  const translatedSessionTypeOptions = React.useMemo(
    () =>
      SESSION_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`sessionType.${option.value}`, option.label),
      })),
    [t]
  );

  const priceTierOptions = React.useMemo(
    () => [
      { value: '', label: t('sessionDetailPage.form.selectPriceTier') },
      ...priceTiers.map((tier) => {
        const formattedPrice =
          tier.currency === 'HUF'
            ? Math.round(tier.price)
            : tier.price.toFixed(2);
        return {
          value: tier.id,
          label: `${tier.tierName} (${tier.currency} ${formattedPrice} - ${tier.durationMinutes}min)`,
        };
      }),
    ],
    [priceTiers, t]
  );

  const logColumns = React.useMemo(
    () => [
      {
        key: 'timestamp',
        header: t('sessionDetailPage.logTable.headers.dateTime'),
        render: (log: ChangeLogEntry) =>
          log.timestamp instanceof Timestamp
            ? log.timestamp.toDate().toLocaleString()
            : 'N/A',
      },
      {
        key: 'actor',
        header: t('sessionDetailPage.logTable.headers.actor'),
        render: (log: ChangeLogEntry) => log.actor,
      },
      {
        key: 'changeType',
        header: t('sessionDetailPage.logTable.headers.changeType'),
        render: (log: ChangeLogEntry) =>
          t(`changeLogType.${log.changeType}`, log.changeType),
      },
      {
        key: 'details',
        header: t('sessionDetailPage.logTable.headers.details'),
        render: (log: ChangeLogEntry) => log.details || '-',
      },
    ],
    [t]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
        <span className="ml-3 text-gray-600">
          {t('sessionDetailPage.status.loading')}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200 text-center">
        <p className="font-medium">{t('sessionDetailPage.errors.title')}</p>
        <p>{error}</p>
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
          className="mt-2"
        >
          {t('sessionDetailPage.buttons.back')}
        </Button>
      </div>
    );
  }

  if (!session || !patient) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg text-yellow-700 border border-yellow-200 text-center">
        <p>{t('sessionDetailPage.errors.notFound')}</p>
        <Button
          variant="link"
          onClick={() => navigate('/patients')}
          className="mt-2"
        >
          {t('patientDetail.notFound.backButton')}
        </Button>
      </div>
    );
  }

  const currentPriceTier = priceTiers.find(
    (tier) => tier.id === (formData?.priceTierId || session.priceTierId)
  );

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => navigate(-1)}
          aria-label={t('sessionDetailPage.buttons.back')}
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-semibold text-gray-900 truncate">
          {t('sessionDetailPage.heading')}
        </h1>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('sessionDetailPage.subHeadingInfo')}
          </h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || !!session.invoiceId}
              title={
                session.invoiceId
                  ? t('sessionDetailPage.tooltips.alreadyInvoiced')
                  : isGeneratingInvoice
                  ? undefined
                  : t('sessionDetailPage.buttons.generateInvoice')
              }
            >
              {isGeneratingInvoice ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  {t('sessionDetailPage.buttons.generatingInvoice')}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t('sessionDetailPage.buttons.generateInvoice')}
                </div>
              )}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveChanges}
              isLoading={isSaving}
              disabled={isSaving}
              size="sm"
            >
              {t('sessionDetailPage.buttons.saveChanges')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="space-y-5">
            <Input
              label={t('sessionDetailPage.form.patientName')}
              value={patient.name}
              readOnly
              className="bg-gray-100 cursor-not-allowed"
            />
            <Input
              label={t('sessionDetailPage.form.dateTime')}
              value={
                session.sessionDateTime instanceof Timestamp
                  ? session.sessionDateTime.toDate().toLocaleString()
                  : 'N/A'
              }
              readOnly
              className="bg-gray-100 cursor-not-allowed"
            />
            <Dropdown
              label={t('sessionDetailPage.form.status')}
              options={translatedStatusOptions}
              value={formData?.status ?? ''}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  status: value as SessionStatus,
                }))
              }
            />
            <Dropdown
              label={t('sessionDetailPage.form.sessionType')}
              options={translatedSessionTypeOptions}
              value={formData?.sessionType ?? ''}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  sessionType: value as SessionType,
                }))
              }
            />
          </div>

          <div className="space-y-5">
            <Dropdown
              label={t('sessionDetailPage.form.priceTier')}
              options={priceTierOptions}
              value={formData?.priceTierId ?? ''}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, priceTierId: value }))
              }
              placeholder={t('sessionDetailPage.form.selectPriceTier')}
            />

            {currentPriceTier && (
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">
                    {t('sessionDetailPage.display.duration')}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {t('sessionDetailPage.display.durationValue', {
                      count: currentPriceTier.durationMinutes,
                    })}
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">
                    {t('sessionDetailPage.display.price')}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {currentPriceTier.currency}{' '}
                    {currentPriceTier.currency === 'HUF'
                      ? Math.round(currentPriceTier.price)
                      : currentPriceTier.price.toFixed(2)}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="session-notes-detail"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                {t('sessionDetailPage.form.notes')}
              </label>
              <textarea
                id="session-notes-detail"
                value={formData?.notes ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                className="w-full px-3 py-2 text-gray-900 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                rows={currentPriceTier ? 3 : 6}
                placeholder={t('sessionDetailPage.form.notesPlaceholder')}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-gray-500" />
          <h2 className="text-xl font-semibold text-gray-900">
            {t('sessionDetailPage.logHeading')}
          </h2>
        </div>

        {logError ? (
          <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
            <p className="font-medium">
              {t('sessionDetailPage.errors.logTitle')}
            </p>
            <p>{logError}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <Table
              columns={logColumns}
              data={changeLogEntries}
              isLoading={isLoadingLog}
              emptyStateMessage={t('sessionDetailPage.logTable.noData')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionDetailPage;